import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, EmptyState, Favicon, Icon, Input, Modal, Skeleton } from '@vault/ui';
import type { Item, ShareLink } from '@vault/shared';
import type { CollectionItemsPage } from '../api/client.js';
import { api } from '../api/client.js';
import {
  ColorPicker,
  ProUpsellModal,
  Swatch,
  errMessage,
  favVar,
  formatDate,
  hashIndex,
  hostname,
  initial,
  isApiStatus,
  useToasts,
} from './_vault/shared.js';

function openUrl(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function CollectionDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toasts = useToasts();

  const [menuOpen, setMenuOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameColor, setRenameColor] = useState<string>(favVar(0));
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [upsell, setUpsell] = useState(false);

  const itemsQuery = useInfiniteQuery({
    queryKey: ['collections', id, 'items'],
    enabled: id !== '',
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      api.collections.items(id, pageParam != null ? { before: pageParam } : {}),
    getNextPageParam: (last: CollectionItemsPage) => last.nextBefore ?? undefined,
  });

  const pages = itemsQuery.data?.pages ?? [];
  const collection = pages[0]?.collection ?? null;
  const items: Item[] = pages.flatMap((p) => p.items);

  const removeItemMut = useMutation({
    mutationFn: (itemId: string) => api.collections.removeItems(id, [itemId]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collections', id] });
      qc.invalidateQueries({ queryKey: ['collections'] });
    },
    onError: (err) => toasts.show({ tone: 'danger', title: 'Could not remove tab', description: errMessage(err) }),
  });

  const renameMut = useMutation({
    mutationFn: (patch: { name?: string; color?: string | null }) => api.collections.rename(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collections', id] });
      qc.invalidateQueries({ queryKey: ['collections'] });
      setRenameOpen(false);
      toasts.show({ tone: 'success', title: 'Collection updated' });
    },
    onError: (err) => {
      if (isApiStatus(err, 402)) {
        setRenameOpen(false);
        setUpsell(true);
        return;
      }
      toasts.show({ tone: 'danger', title: 'Could not update collection', description: errMessage(err) });
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => api.collections.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collections'] });
      toasts.show({ tone: 'success', title: 'Collection deleted' });
      navigate('/collections');
    },
    onError: (err) => toasts.show({ tone: 'danger', title: 'Could not delete collection', description: errMessage(err) }),
  });

  const shareMut = useMutation({
    mutationFn: () => api.share.create('collection', id),
    onSuccess: (res) => {
      setShareLink(res.share);
      toasts.show({ tone: 'success', title: 'Share link ready', description: 'Anyone with the link can view — read-only.' });
    },
    onError: (err) => {
      if (isApiStatus(err, 402)) {
        setUpsell(true);
        return;
      }
      toasts.show({ tone: 'danger', title: 'Could not create share link', description: errMessage(err) });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (token: string) => api.share.revoke(token),
    onSuccess: () => {
      setShareLink(null);
      toasts.show({ tone: 'success', title: 'Share link revoked' });
    },
    onError: (err) => toasts.show({ tone: 'danger', title: 'Could not revoke link', description: errMessage(err) }),
  });

  if (id === '') return <Navigate to="/collections" replace />;

  function openRename() {
    if (!collection) return;
    setRenameName(collection.name);
    setRenameColor(collection.color ?? favVar(hashIndex(collection.id, 8)));
    setRenameOpen(true);
  }
  function submitRename() {
    const trimmed = renameName.trim();
    if (!trimmed || renameMut.isPending) return;
    renameMut.mutate({ name: trimmed, color: renameColor });
  }
  function copyShare() {
    if (!shareLink) return;
    void navigator.clipboard?.writeText(shareLink.url);
    toasts.show({ tone: 'success', title: 'Link copied' });
  }
  function restoreAll() {
    if (items.length === 0) return;
    for (const it of items) openUrl(it.url);
    const total = collection?.itemCount ?? items.length;
    toasts.show({
      tone: 'info',
      title: items.length < total ? `Opening ${items.length} of ${total} loaded tabs` : `Opening ${items.length} tabs`,
      description: 'Your browser may ask to allow multiple tabs.',
    });
  }

  const notFound =
    !itemsQuery.isLoading && !itemsQuery.isError && collection === null;

  return (
    <div className="cold">
      <style>{CSS}</style>

      <button type="button" className="cold-back" onClick={() => navigate('/collections')}>
        <Icon name="chevron-left" size={12} />
        Collections
      </button>

      {itemsQuery.isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Skeleton width={220} height={22} radius={8} />
          <Skeleton width={320} height={12} radius={6} />
          <Skeleton height={240} radius={12} />
        </div>
      )}

      {itemsQuery.isError && (
        <div className="cold-error">
          <span style={{ fontSize: 13, fontWeight: 600 }}>Couldn’t load this collection</span>
          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{errMessage(itemsQuery.error)}</span>
          <Button variant="ghost" size="sm" onClick={() => itemsQuery.refetch()}>Try again</Button>
        </div>
      )}

      {notFound && (
        <div style={{ maxWidth: 420, margin: '40px auto' }}>
          <EmptyState
            dashed={false}
            icon={<Icon name="folder" size={18} color="var(--text-3)" />}
            title="Collection not found"
            description="It may have been deleted. Head back to your collections."
            action={
              <div style={{ marginTop: 4 }}>
                <Button variant="ghost" size="sm" onClick={() => navigate('/collections')}>Back to collections</Button>
              </div>
            }
          />
        </div>
      )}

      {collection && (
        <>
          <div className="cold-head">
            <Swatch seed={collection.id} color={collection.color} size={13} />
            <span className="cold-title">{collection.name}</span>
            <button type="button" className="cold-menu-btn" onClick={() => setMenuOpen((v) => !v)} aria-label="Collection options">
              <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1, lineHeight: 0 }}>⋯</span>
            </button>
            {menuOpen && (
              <>
                <div className="cold-menu-scrim" onClick={() => setMenuOpen(false)} />
                <div className="cold-menu">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      openRename();
                    }}
                  >
                    <Icon name="gear" size={13} /> Rename &amp; recolour
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => {
                      setMenuOpen(false);
                      setDeleteOpen(true);
                    }}
                  >
                    <Icon name="trash" size={13} /> Delete collection
                  </button>
                </div>
              </>
            )}
          </div>

          <span className="cold-metaline">
            <span>{collection.itemCount} TAB{collection.itemCount === 1 ? '' : 'S'}</span>
            <span>·</span>
            {collection.kind === 'ai' ? (
              <Badge tone="warn" pill={false}>
                <Icon name="sparkle" size={9} color="var(--amber-ink)" /> Auto
              </Badge>
            ) : (
              <Badge tone="neutral" pill={false}>Manual</Badge>
            )}
            <span>·</span>
            <span>UPDATED {formatDate(collection.updatedAt)}</span>
          </span>

          <div className="cold-actions">
            <span style={{ flex: 1 }}>
              <Button
                variant="secondary"
                fullWidth
                icon={<Icon name="restore" size={13} />}
                onClick={restoreAll}
                disabled={items.length === 0}
              >
                Restore all {collection.itemCount}
              </Button>
            </span>
            {shareLink ? (
              <button type="button" className="cold-accent-btn" onClick={copyShare}>
                <Icon name="link" size={12} /> Copy link
              </button>
            ) : (
              <button type="button" className="cold-accent-btn" onClick={() => !shareMut.isPending && shareMut.mutate()}>
                <Icon name="share" size={12} /> Share
              </button>
            )}
          </div>

          {shareLink && (
            <div className="cold-share-row">
              <span className="cold-share-link">
                <Icon name="link" size={11} color="var(--success)" />
                {shareLink.url}
              </span>
              <button type="button" className="cold-chip" onClick={copyShare}>Copy</button>
              <button
                type="button"
                className="cold-chip"
                onClick={() => !revokeMut.isPending && revokeMut.mutate(shareLink.token)}
              >
                Revoke
              </button>
              <span className="cold-ro">
                <span className="cold-ro-dot" />
                READ-ONLY
              </span>
            </div>
          )}

          {items.length === 0 ? (
            <EmptyState
              dashed
              icon={<Icon name="folder" size={18} color="var(--text-3)" />}
              title="No tabs in this collection yet"
              description="Add tabs from your timeline or search and they’ll show up here."
            />
          ) : (
            <div className="cold-list">
              {items.map((it) => (
                <div key={it.id} className="cold-row">
                  <a className="cold-row-link" href={it.url} target="_blank" rel="noopener noreferrer">
                    <Favicon letter={initial(it.title || hostname(it.url))} index={hashIndex(it.id, 8)} size={18} ring="transparent" />
                    <span className="cold-row-main">
                      <span className="cold-row-title">{it.title || hostname(it.url)}</span>
                      <span className="cold-row-sub">{hostname(it.url)} · {formatDate(it.updatedAt)}</span>
                    </span>
                    <Icon name="external" size={12} color="var(--text-3)" />
                  </a>
                  <button
                    type="button"
                    className="cold-row-remove"
                    aria-label="Remove from collection"
                    onClick={() => {
                      if (!removeItemMut.isPending) removeItemMut.mutate(it.id);
                    }}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {itemsQuery.hasNextPage && (
            <button
              type="button"
              className="cold-more"
              onClick={() => itemsQuery.fetchNextPage()}
              disabled={itemsQuery.isFetchingNextPage}
            >
              {itemsQuery.isFetchingNextPage
                ? 'Loading…'
                : `Show ${Math.max(0, collection.itemCount - items.length)} more`}
            </button>
          )}
        </>
      )}

      <Modal
        open={renameOpen}
        width={440}
        title="Rename collection"
        onClose={() => setRenameOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={renameMut.isPending} disabled={!renameName.trim()} onClick={submitRename}>
              Save changes
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input label="Name" value={renameName} onChange={(e) => setRenameName(e.target.value)} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Colour</span>
            <ColorPicker value={renameColor} onChange={setRenameColor} />
          </div>
        </div>
      </Modal>

      <Modal
        open={deleteOpen}
        width={420}
        title="Delete this collection?"
        subtitle="The grouping is removed — your saved tabs stay in your vault."
        onClose={() => setDeleteOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Keep it</Button>
            <Button variant="danger" loading={deleteMut.isPending} onClick={() => deleteMut.mutate()}>
              Delete collection
            </Button>
          </>
        }
      >
        <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.55 }}>
          {collection
            ? `“${collection.name}” has ${collection.itemCount} tab${collection.itemCount === 1 ? '' : 's'}. This only removes the grouping — nothing is deleted from your timeline.`
            : 'This only removes the grouping — nothing is deleted from your timeline.'}
        </span>
      </Modal>

      <ProUpsellModal
        open={upsell}
        onClose={() => setUpsell(false)}
        title="This needs Pro"
        body="Sharing and AI-recoloured collections are part of Pro. Upgrade to share read-only links and let AI keep your collections organised."
      />
      {toasts.node}
    </div>
  );
}

const CSS = `
.cold { padding: 24px 28px; max-width: 760px; margin: 0 auto; display: flex; flex-direction: column; gap: 14px; }
.cold-back { display: inline-flex; align-items: center; gap: 6px; font-family: var(--font-body); font-size: 12px; font-weight: 600; color: var(--text-2); background: none; border: none; cursor: pointer; padding: 0; width: fit-content; }
.cold-back:hover { color: var(--text-1); }
.cold-head { display: flex; align-items: center; gap: 10px; position: relative; }
.cold-title { font-family: var(--font-display-cond); font-size: 20px; font-weight: 700; letter-spacing: .02em; text-transform: uppercase; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cold-menu-btn { margin-left: auto; display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 8px; color: var(--text-3); border: 1px solid transparent; background: transparent; cursor: pointer; flex-shrink: 0; }
.cold-menu-btn:hover { background: var(--surface-2); color: var(--text-1); }
.cold-menu-scrim { position: fixed; inset: 0; z-index: 10; }
.cold-menu { position: absolute; right: 0; top: 36px; z-index: 20; min-width: 178px; background: var(--surface-2); border: 1px solid var(--line-strong); border-radius: 10px; box-shadow: var(--shadow-pop); padding: 6px; display: flex; flex-direction: column; gap: 2px; }
.cold-menu button { display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: 7px; font-family: var(--font-body); font-size: 12.5px; color: var(--text-1); background: none; border: none; cursor: pointer; text-align: left; }
.cold-menu button:hover { background: var(--surface-3); }
.cold-menu button.danger { color: var(--danger); }
.cold-metaline { font-family: var(--font-mono); font-size: 10px; color: var(--text-3); display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cold-actions { display: flex; gap: 8px; align-items: stretch; }
.cold-accent-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid var(--line-accent); background: var(--accent-tint); border-radius: 999px; padding: 0 16px; height: 38px; font-family: var(--font-body); font-size: 12.5px; font-weight: 600; color: var(--accent-ink); cursor: pointer; white-space: nowrap; }
.cold-accent-btn:hover { background: var(--surface-2); }
.cold-share-row { display: flex; align-items: center; gap: 9px; background: var(--surface-1); border: 1px solid var(--line-accent); border-radius: 10px; padding: 8px 10px 8px 12px; }
.cold-share-link { display: inline-flex; align-items: center; gap: 6px; font-family: var(--font-mono); font-size: 10px; color: var(--text-2); flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cold-chip { display: inline-flex; align-items: center; border: 1px solid var(--line-strong); border-radius: 7px; padding: 5px 10px; font-family: var(--font-body); font-size: 11px; font-weight: 600; color: var(--text-1); cursor: pointer; background: transparent; white-space: nowrap; }
.cold-chip:hover { background: var(--surface-2); }
.cold-ro { display: inline-flex; align-items: center; gap: 5px; font-family: var(--font-mono); font-size: 8.5px; color: var(--text-3); white-space: nowrap; }
.cold-ro-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--success); }
.cold-list { display: flex; flex-direction: column; background: var(--surface-1); border: 1px solid var(--line); border-radius: 12px; overflow: hidden; }
.cold-row { display: flex; align-items: center; border-bottom: 1px solid var(--line); }
.cold-row:last-child { border-bottom: none; }
.cold-row:hover { background: var(--surface-2); }
.cold-row-link { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; padding: 11px 8px 11px 13px; text-decoration: none; color: inherit; cursor: pointer; }
.cold-row-link:focus-visible { outline: 2px solid var(--line-accent); outline-offset: -2px; border-radius: 8px; }
.cold-row-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.cold-row-title { font-size: 12.5px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--text-1); }
.cold-row-sub { font-family: var(--font-mono); font-size: 9px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cold-row-remove { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 7px; margin-right: 13px; color: var(--text-3); border: none; background: transparent; cursor: pointer; opacity: 0; flex-shrink: 0; }
.cold-row:hover .cold-row-remove { opacity: 1; }
.cold-row-remove:hover { background: var(--surface-3); color: var(--danger); }
.cold-row-remove:focus-visible { opacity: 1; }
.cold-more { align-self: center; font-family: var(--font-body); font-size: 11.5px; font-weight: 600; color: var(--primary); background: none; border: none; cursor: pointer; padding: 8px; }
.cold-more:disabled { opacity: .6; cursor: default; }
.cold-error { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; background: var(--surface-1); border: 1px solid var(--line); border-radius: 14px; padding: 20px; }
@media (max-width: 560px) {
  .cold { padding: 16px; }
}
`;
